import React, { Fragment, useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowLeft, faCircle, faCopy, faEnvelope, faExclamationCircle, faExternalLinkAlt, faFileInvoice, faLink, faPhone, faPrint,
} from "@fortawesome/free-solid-svg-icons";
import util from "../../utils/miniUtils";
import { ADMIN_USER } from "../../utils/constants";
import ShipOrderForm from "./ShipOrderForm";
import ResolveAlertForm from "./ResolveAlertForm";
import InvoiceUploadForm from "./InvoiceUploadForm";
import CancelOrderForm from "./CancelOrderForm";
import {
    CANCELLED_BY_LABELS, INVOICE_FILE_TYPE, saveBlobAsFile, ALERT_LABELS, EVENT_LABELS, MERCADOPAGO_PANEL_URL, ORDER_EVENT_NOTE, SHIPPING_LABELS, SHIPPING_STATUS, addressLines, addressToText, formatDateTimeHrs,
    formatMxn, formatPhoneMx, isPaymentFinal, isWebLink, paymentBadgeClass, paymentDetailLabel, paymentStatusLabel, shippingBadgeClass, showApiError,
    todayInBusinessTimezone,
} from "./salesHelpers";
import "./Sales.css";

const SHIP_FORM = { NONE: "none", SHIP: "ship", CORRECTION: "correction" };

// Detalle del pedido (§7.2) como página completa en /ventas/:uuid: piezas, cliente, dirección, pagos, envío, devoluciones, historial y alertas.
// Acciones de envío para Admin y Empleado; atender alertas solo Admin.
export default function SaleDetail() {
    const { uuid } = useParams();
    const history = useHistory();
    const location = useLocation();
    // La lista nos manda su query (página, filtros) para regresar al mismo punto
    const listSearch = location.state && location.state.listSearch ? location.state.listSearch : "";
    const [ order, setOrder ] = useState( null );
    const [ loading, setLoading ] = useState( false );
    const [ shipForm, setShipForm ] = useState( SHIP_FORM.NONE );
    const [ deliveredOn, setDeliveredOn ] = useState( todayInBusinessTimezone() );
    const [ noteText, setNoteText ] = useState( "" );
    const [ savingNote, setSavingNote ] = useState( false );
    const [ refreshingPayment, setRefreshingPayment ] = useState( false );
    const [ showCancelForm, setShowCancelForm ] = useState( false );
    const isAdmin = util.user.role === ADMIN_USER;

    useEffect( () => {
        loadOrder().then( null );
    }, [ uuid ] );

    async function loadOrder() {
        try {
            setLoading( true );
            const request = await util.axios.get( `sales/orders/${uuid}` );
            const { error, msg, data } = request.data;
            if ( error ) throw msg;
            setOrder( data );
        } catch ( e ) {
            showApiError( e );
        } finally {
            setLoading( false );
        }
    }

    // Tras cualquier acción: recargar el detalle (cambian Envío y alertas)
    async function reloadAfterChange() {
        setShipForm( SHIP_FORM.NONE );
        setShowCancelForm( false );
        await loadOrder();
    }

    // El archivo viaja por la API con la sesión del panel: el contenedor de facturas es privado
    async function downloadInvoice( fileType ) {
        try {
            const request = await util.axios.get( `sales/orders/${uuid}/invoice/${fileType}`, { responseType: "blob" } );
            saveBlobAsFile( request.data, `factura-${order.orderNumber}.${fileType}` );
        } catch ( e ) {
            showApiError( e );
        }
    }

    async function addNote() {
        try {
            setSavingNote( true );
            const request = await util.axios.post( `sales/orders/${uuid}/notes`, { note: noteText } );
            const { error, msg } = request.data;
            if ( error ) throw msg;
            util.toast().success( msg );
            setNoteText( "" );
            await loadOrder();
        } catch ( e ) {
            showApiError( e );
        } finally {
            setSavingNote( false );
        }
    }

    // Consulta a MercadoPago el estado real del pago y recarga el detalle (el webhook o la conciliación pudieron tardar)
    async function refreshPayment() {
        try {
            setRefreshingPayment( true );
            const request = await util.axios.post( `sales/orders/${uuid}/reconcile-payment` );
            const { error, msg } = request.data;
            if ( error ) throw msg;
            util.toast().success( msg );
            await reloadAfterChange();
        } catch ( e ) {
            showApiError( e );
        } finally {
            setRefreshingPayment( false );
        }
    }

    async function markDelivered() {
        try {
            setLoading( true );
            const request = await util.axios.post( `sales/orders/${uuid}/deliver`, { deliveredOn } );
            const { error, msg } = request.data;
            if ( error ) throw msg;
            util.toast().success( msg );
            await reloadAfterChange();
        } catch ( e ) {
            showApiError( e );
        } finally {
            setLoading( false );
        }
    }

    // El ID se pega en el buscador de actividad del panel de MercadoPago
    async function copyPaymentId( providerPaymentId ) {
        try {
            await navigator.clipboard.writeText( String( providerPaymentId ) );
            util.toast().success( "ID de pago copiado" );
        } catch ( e ) {
            util.handleError( "No se pudo copiar el ID" );
        }
    }

    async function copyAddress() {
        try {
            await navigator.clipboard.writeText( addressToText( order.shippingAddress ) );
            util.toast().success( "Dirección copiada" );
        } catch ( e ) {
            util.handleError( "No se pudo copiar la dirección" );
        }
    }

    return (
        <div className="sales-detail-page container-fluid" data-cy="sales-detail">
            <button type="button" className="btn btn-link text-muted text-decoration-none p-0 mb-2 sales-no-print" onClick={() => history.push( `/ventas${listSearch}` )} data-cy="sales-detail-back">
                <FontAwesomeIcon icon={faArrowLeft} /> Volver a la lista de pedidos
            </button>
            {order && renderOrder()}
            {util.LOADING_SCREEN( loading )}
        </div>
    );

    function renderOrder() {
        const { shipping, shippingAddress, customer } = order;
        const openAlerts = order.alerts.filter( ( alert ) => !alert.resolvedAt );
        const resolvedAlerts = order.alerts.filter( ( alert ) => alert.resolvedAt );
        const notes = order.history.filter( ( entry ) => entry.event === ORDER_EVENT_NOTE );
        const isPaymentRejected = order.paymentStatus === "rejected";
        const { invoice } = order;
        const orderIsPaid = Boolean( shipping.status );
        const invoicePending = invoice.requested && !invoice.invoicedAt && !order.cancellation;
        const canUploadInvoice = invoicePending && orderIsPaid;

        return (
            <Fragment>
                <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
                    <div>
                        <h3 className="mb-1">Pedido #{order.orderNumber}</h3>
                        <div className="text-muted small mb-2">Creado el {formatDateTimeHrs( order.createdAt )}</div>
                        <div className="d-flex flex-wrap gap-2" data-cy="sales-detail-header">
                            {order.cancellation && <span className="badge bg-dark" data-cy="sales-detail-cancelled">Cancelado</span>}
                            {shipping.status && <span className={shippingBadgeClass( shipping.status )}>{SHIPPING_LABELS[shipping.status]}</span>}
                            <span className={paymentBadgeClass( order.paymentStatus )} data-cy="sales-detail-payment-status" title={order.paymentStatus || ""}>
                                MercadoPago: {order.paymentStatus ? paymentStatusLabel( order.paymentStatus ) : "Sin pago"}
                            </span>
                        </div>
                    </div>
                    <div className="d-flex gap-2 sales-no-print">
                        <button type="button" className="btn btn-outline-secondary" onClick={() => window.print()} data-cy="sales-print">
                            <FontAwesomeIcon icon={faPrint} /> Imprimir orden
                        </button>
                    </div>
                </div>

                {openAlerts.length > 0 && (
                    <div className="alert alert-danger" data-cy="sales-detail-open-alerts">
                        <h6 className="mb-2">Requiere atención</h6>
                        {openAlerts.map( ( alert ) => (
                            <div key={alert.id} className="sales-alert-item">
                                <strong>{ALERT_LABELS[alert.reason] || alert.reason}</strong>
                                <span className="small ms-2">{formatDateTimeHrs( alert.createdAt )}</span>
                                {isAdmin && <ResolveAlertForm alert={alert} onSaved={reloadAfterChange} />}
                            </div>
                        ) )}
                    </div>
                )}

                {invoicePending && (
                    <div className="alert alert-warning d-flex align-items-center sales-no-print" data-cy="sales-detail-invoice-pending">
                        <FontAwesomeIcon icon={faFileInvoice} className="sales-invoice-pending me-2" />
                        <span>El cliente <strong>solicitó factura</strong>. Sube el PDF y el XML en el bloque Factura.</span>
                    </div>
                )}

                {order.cancellation && (
                    <div className="alert alert-dark" data-cy="sales-detail-cancellation">
                        <strong>Pedido cancelado</strong> por {CANCELLED_BY_LABELS[order.cancellation.cancelledBy] || order.cancellation.cancelledBy} el{" "}
                        {formatDateTimeHrs( order.cancellation.cancelledAt )}.
                        {order.cancellation.reason && <div className="small mt-1">Motivo: {order.cancellation.reason}</div>}
                        <div className="small mt-1">Reembolsa el pago en MercadoPago (copia el ID de pago) y registra la devolución.</div>
                    </div>
                )}

                <div className="row g-4">
                    <div className="col-lg-8">
                        <div className="card sales-card shadow-sm mb-4">
                            <div className="card-header sales-card-title">Artículos y piezas</div>
                            <div className="table-responsive">
                                <table className="table table-hover table-borderless align-middle mb-0">
                                    <thead>
                                        <tr><th /><th>Producto / Descripción</th><th>Local</th><th className="text-end">Cant.</th><th className="text-end">Precio unitario</th><th className="text-end">Subtotal</th></tr>
                                    </thead>
                                    <tbody>
                                        {order.items.map( ( item ) => (
                                            <tr key={item.orderItemId}>
                                                <td>{item.imageUrl && <img src={item.imageUrl} alt={item.article} className="sales-item-image" />}</td>
                                                <td>
                                                    <div className="fw-semibold">{item.article} {renderInternalId( item.internalId )}</div>
                                                    <div className="small" data-cy={`sales-item-part-number-${item.orderItemId}`}>Pieza: <strong>#{item.productId}</strong></div>
                                                    {item.description && <div className="small text-muted">{item.description}</div>}
                                                </td>
                                                <td>{item.location.name || item.location.code || "—"}</td>
                                                <td className="text-end">{item.quantity}</td>
                                                <td className="text-end sales-money">{formatMxn( item.unitPrice )}</td>
                                                <td className="text-end sales-money">{formatMxn( item.unitPrice * item.quantity )}</td>
                                            </tr>
                                        ) )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="row g-4 mb-4">
                            <div className="col-md-6">
                                <div className="card sales-card shadow-sm h-100">
                                    <div className="card-header sales-card-title">Envío</div>
                                    <div className="card-body">{renderShipping( shipping )}</div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="card sales-card shadow-sm h-100">
                                    <div className="card-header sales-card-title">Devoluciones</div>
                                    <div className="card-body">
                                        {order.returns.length === 0 && <div className="text-muted">Sin devoluciones</div>}
                                        {order.returns.map( ( itemReturn ) => (
                                            <div key={itemReturn.id} className="small">
                                                {formatDateTimeHrs( itemReturn.createdAt )} · <code>{itemReturn.providerRefundId}</code> · {formatMxn( itemReturn.amount )} · {itemReturn.reason}
                                            </div>
                                        ) )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card sales-card shadow-sm mb-4">
                            <div className="card-header sales-card-title d-flex align-items-center gap-2">
                                <span>Pagos y pasarela (MercadoPago)</span>
                                <div className="ms-auto d-flex gap-2 sales-no-print">
                                    <a className="btn btn-sm btn-outline-secondary" href={MERCADOPAGO_PANEL_URL} target="_blank" rel="noopener noreferrer" data-cy="sales-open-mercadopago">
                                        <FontAwesomeIcon icon={faExternalLinkAlt} /> Abrir MercadoPago
                                    </a>
                                    {!isPaymentFinal( order.paymentStatus ) && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={refreshPayment}
                                            disabled={refreshingPayment}
                                            data-cy="sales-refresh-payment"
                                        >
                                            {refreshingPayment ? "Consultando…" : "Actualizar pago"}
                                        </button>
                                    )}
                                </div>
                            </div>
                            {isPaymentRejected && (
                                <div className="alert alert-danger d-flex align-items-center m-3 mb-0" data-cy="sales-detail-payment-rejected">
                                    <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
                                    <span>
                                        Pago rechazado
                                        {order.paymentStatusDetail && `: ${paymentDetailLabel( order.paymentStatus, order.paymentStatusDetail )}`}
                                    </span>
                                </div>
                            )}
                            {order.payments.length === 0 && <div className="card-body text-muted">Sin intentos de pago</div>}
                            {order.payments.length > 0 && (
                                <div className="table-responsive">
                                    <table className="table table-borderless align-middle mb-0 sales-payments-table">
                                        <thead>
                                            <tr><th>ID MercadoPago</th><th>Estado</th><th>Motivo / Detalle</th><th className="text-end">Monto</th><th>Fecha</th></tr>
                                        </thead>
                                        <tbody>
                                            {order.payments.map( ( payment ) => (
                                                <tr key={payment.providerPaymentId}>
                                                    <td className="text-nowrap">
                                                        {payment.providerPaymentId}
                                                        <button type="button" className="btn btn-link btn-sm py-0 sales-no-print" title="Copiar ID" aria-label="Copiar ID de pago"
                                                            onClick={() => copyPaymentId( payment.providerPaymentId )} data-cy={`sales-copy-payment-${payment.providerPaymentId}`}>
                                                            <FontAwesomeIcon icon={faCopy} />
                                                        </button>
                                                    </td>
                                                    <td><span className={paymentBadgeClass( payment.status )} title={payment.status}>{paymentStatusLabel( payment.status )}</span></td>
                                                    <td className="small text-muted">{payment.statusDetail && paymentDetailLabel( payment.status, payment.statusDetail )}</td>
                                                    <td className="text-end sales-money">{formatMxn( payment.amount )}</td>
                                                    <td className="text-nowrap">{formatDateTimeHrs( payment.createdAt )}</td>
                                                </tr>
                                            ) )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {resolvedAlerts.length > 0 && (
                            <div className="card sales-card shadow-sm mb-4">
                                <div className="card-header sales-card-title">Alertas atendidas</div>
                                <div className="card-body">
                                    {resolvedAlerts.map( ( alert ) => (
                                        <div key={alert.id} className="small">
                                            {ALERT_LABELS[alert.reason] || alert.reason} · {alert.resolvedByName} · {formatDateTimeHrs( alert.resolvedAt )} · {alert.resolutionNote}
                                        </div>
                                    ) )}
                                </div>
                            </div>
                        )}

                        <div className="card sales-card shadow-sm mb-4 sales-no-print">
                            <div className="card-header sales-card-title">Actividad y notas internas</div>
                            <div className="card-body sales-no-print">
                                <textarea
                                    className="form-control mb-2"
                                    rows={2}
                                    placeholder="Agregar nota..."
                                    value={noteText}
                                    onChange={( event ) => setNoteText( event.target.value )}
                                    data-cy="sales-note-input"
                                />
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={addNote}
                                    disabled={savingNote || noteText.trim().length === 0}
                                    data-cy="sales-note-save"
                                >
                                    {savingNote ? "Guardando…" : "Guardar nota"}
                                </button>
                            </div>
                            <ul className="list-group list-group-flush sales-timeline">
                                {order.history.map( ( entry, position ) => (
                                    // El historial no trae id y solo se agrega al final: la posición es estable
                                    <li key={position} className="list-group-item d-flex gap-3">
                                        <FontAwesomeIcon icon={faCircle} className="sales-timeline-dot mt-2" />
                                        <div>
                                            <div className="small">
                                                <strong>{EVENT_LABELS[entry.event] || entry.event}</strong>
                                                <span className="text-muted"> · {entry.actorName || entry.actorType}</span>
                                            </div>
                                            {entry.notes && <div>{entry.notes}</div>}
                                            <div className="small text-muted">{formatDateTimeHrs( entry.createdAt )}</div>
                                        </div>
                                    </li>
                                ) )}
                            </ul>
                            {notes.length === 0 && <div className="card-footer small text-muted">Sin notas internas</div>}
                        </div>
                    </div>

                    <div className="col-lg-4">
                        <div className="card sales-card shadow-sm mb-4">
                            <div className="card-header sales-card-title">Cliente</div>
                            <div className="card-body">
                                <div className="fw-semibold mb-1">{customer.firstName} {customer.lastName}</div>
                                <div className="text-break"><FontAwesomeIcon icon={faEnvelope} className="text-muted me-2" />{customer.email}</div>
                                {customer.phone && <div><FontAwesomeIcon icon={faPhone} className="text-muted me-2" />{formatPhoneMx( customer.phone )}</div>}
                                <a className="btn btn-sm btn-light border w-100 mt-3 sales-no-print" href={`mailto:${customer.email}`} data-cy="sales-contact-customer">
                                    <FontAwesomeIcon icon={faEnvelope} /> Contactar cliente
                                </a>
                            </div>
                        </div>

                        {invoice.requested && (
                            <div className="card sales-card shadow-sm mb-4" data-cy="sales-invoice-card">
                                <div className="card-header sales-card-title">Factura</div>
                                <div className="card-body">
                                    {renderTaxData( invoice.taxData )}
                                    {invoice.invoicedAt && (
                                        <div className="mt-3 sales-no-print" data-cy="sales-invoice-done">
                                            <div className="small text-muted mb-2">Facturado el {formatDateTimeHrs( invoice.invoicedAt )}</div>
                                            <div className="d-flex gap-2">
                                                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => downloadInvoice( INVOICE_FILE_TYPE.PDF )} data-cy="sales-invoice-download-pdf">Descargar PDF</button>
                                                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => downloadInvoice( INVOICE_FILE_TYPE.XML )} data-cy="sales-invoice-download-xml">Descargar XML</button>
                                            </div>
                                        </div>
                                    )}
                                    {canUploadInvoice && <InvoiceUploadForm order={order} onSaved={reloadAfterChange} />}
                                    {invoicePending && !orderIsPaid && <div className="small text-muted mt-3">Se podrá subir cuando el pago esté aprobado.</div>}
                                </div>
                            </div>
                        )}

                        <div className="card sales-card shadow-sm mb-4">
                            <div className="card-header sales-card-title">Dirección de envío</div>
                            <div className="card-body">
                                {renderAddress( shippingAddress )}
                                {shippingAddress && (
                                    <button type="button" className="btn btn-sm btn-light border w-100 mt-3 sales-no-print" onClick={copyAddress} data-cy="sales-copy-address">
                                        <FontAwesomeIcon icon={faCopy} /> Copiar dirección
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="card sales-card shadow-sm mb-4">
                            <div className="card-header sales-card-title">Resumen financiero</div>
                            <div className="card-body sales-money">
                                <div className="d-flex justify-content-between"><span>Subtotal</span><span>{formatMxn( order.subtotal )}</span></div>
                                {order.tax > 0 && <div className="d-flex justify-content-between text-muted small" data-cy="sales-detail-tax"><span>Incluye IVA</span><span>{formatMxn( order.tax )}</span></div>}
                                <div className="d-flex justify-content-between"><span>Envío</span><span>{formatMxn( order.shippingCost )}</span></div>
                                <hr className="my-2" />
                                <div className="d-flex justify-content-between fs-4 fw-bold text-primary"><span>Total</span><span>{formatMxn( order.total )}</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    }

    // internal_id puede ser un link de MercadoLibre, otro texto o estar vacío
    function renderInternalId( internalId ) {
        if ( !internalId ) return null;
        if ( !isWebLink( internalId ) ) return <span className="small text-muted">({internalId})</span>;
        return (
            <a href={internalId.trim()} target="_blank" rel="noopener noreferrer" title={internalId.trim()} aria-label="Abrir publicación" data-cy="sales-item-link">
                <FontAwesomeIcon icon={faLink} />
            </a>
        );
    }

    // Copia de los datos fiscales que el cliente usó al pedir la factura (ya no cambia si él los edita después)
    function renderTaxData( taxData ) {
        if ( !taxData ) return <div className="text-muted">Sin datos fiscales</div>;
        return (
            <div data-cy="sales-invoice-tax-data">
                <div className="fw-semibold">{taxData.legalName}</div>
                <div>RFC: {taxData.rfc}</div>
                <div>Régimen fiscal: {taxData.taxRegime}</div>
                <div>Uso de CFDI: {taxData.cfdiUse}</div>
                <div>C.P. fiscal: {taxData.zipCode}</div>
            </div>
        );
    }

    function renderAddress( address ) {
        if ( !address ) return <div className="text-muted">—</div>;
        const lines = addressLines( address );
        return (
            <Fragment>
                {address.recipientName && <div className="fw-semibold">{address.recipientName}</div>}
                {address.recipientPhone && <div>{formatPhoneMx( address.recipientPhone )}</div>}
                {lines.map( ( line ) => <div key={line}>{line}</div> )}
                {address.betweenStreets && <div className="small text-muted mt-2">Entre: {address.betweenStreets}</div>}
                {address.addressReferences && <div className="small text-muted">Ref.: {address.addressReferences}</div>}
            </Fragment>
        );
    }

    function renderShipping( shipping ) {
        // Sin pago approved el pedido todavía no tiene columna Envío
        if ( !shipping.status ) return <div className="text-muted">Sin envío: el pago no está aprobado</div>;

        const isToShip = shipping.status === SHIPPING_STATUS.TO_SHIP;
        const isShipped = shipping.status === SHIPPING_STATUS.SHIPPED;
        const isDelivered = shipping.status === SHIPPING_STATUS.DELIVERED;

        return (
            <div data-cy="sales-detail-shipping">
                <div>Estado: <span className={shippingBadgeClass( shipping.status )}>{SHIPPING_LABELS[shipping.status]}</span></div>
                {shipping.carrier && <div>Paquetería: {shipping.carrier} · Guía: {shipping.trackingNumber}</div>}
                {shipping.shippedAt && <div>Enviado: {formatDateTimeHrs( shipping.shippedAt )} ({shipping.daysSinceShipment} días)</div>}
                {isDelivered && <div>Entregado: {formatDateTimeHrs( shipping.deliveredAt )} ({shipping.daysSinceDelivery} días)</div>}

                {isToShip && !order.cancellation && shipForm === SHIP_FORM.NONE && !showCancelForm && (
                    <button type="button" className="btn btn-sm btn-primary mt-2 sales-no-print" onClick={() => setShipForm( SHIP_FORM.SHIP )} data-cy="sales-ship-open">
                        Marcar Enviado
                    </button>
                )}
                {isShipped && shipForm === SHIP_FORM.NONE && (
                    <div className="d-flex flex-wrap align-items-end gap-2 mt-2 sales-no-print">
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setShipForm( SHIP_FORM.CORRECTION )} data-cy="sales-tracking-open">
                            Corregir guía
                        </button>
                        <div>
                            <label htmlFor="sales-delivered-on" className="form-label small mb-0">Fecha de entrega</label>
                            <input
                                id="sales-delivered-on"
                                type="date"
                                className="form-control form-control-sm"
                                value={deliveredOn}
                                max={todayInBusinessTimezone()}
                                onChange={( event ) => setDeliveredOn( event.target.value )}
                                data-cy="sales-delivered-on"
                            />
                        </div>
                        <button type="button" className="btn btn-sm btn-success" onClick={markDelivered} disabled={!deliveredOn} data-cy="sales-deliver">
                            Marcar Entregado
                        </button>
                    </div>
                )}
                {isToShip && order.invoice.invoicedAt && !order.cancellation && (
                    <div className="small text-muted mt-2" data-cy="sales-cancel-blocked-invoiced">
                        Ya está facturado: no se puede cancelar desde el sistema. Cancela primero la factura en el SAT.
                    </div>
                )}
                {isToShip && isAdmin && !order.cancellation && !order.invoice.invoicedAt && shipForm === SHIP_FORM.NONE && !showCancelForm && (
                    <button type="button" className="btn btn-sm btn-outline-danger mt-2 ms-2 sales-no-print" onClick={() => setShowCancelForm( true )} data-cy="sales-cancel-open">
                        Cancelar pedido
                    </button>
                )}
                {showCancelForm && <CancelOrderForm order={order} onSaved={reloadAfterChange} onCancel={() => setShowCancelForm( false )} />}
                {shipForm !== SHIP_FORM.NONE && (
                    <ShipOrderForm
                        order={order}
                        isCorrection={shipForm === SHIP_FORM.CORRECTION}
                        onSaved={reloadAfterChange}
                        onCancel={() => setShipForm( SHIP_FORM.NONE )}
                    />
                )}
            </div>
        );
    }
}
